"use client"

import { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  IconButton,
  ThemeProvider,
  createTheme,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  InputAdornment,
  alpha,
  Tooltip,
  FormControl,
  InputLabel,
  Fade,
  Alert,
  Drawer,
  Divider,
  Badge,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"
import VisibilityIcon from "@mui/icons-material/Visibility"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import PersonIcon from "@mui/icons-material/Person"
import PriceChangeIcon from "@mui/icons-material/PriceChange"

import ClearIcon from "@mui/icons-material/Clear"
import EditIcon from "@mui/icons-material/Edit"
import SaveIcon from "@mui/icons-material/Save"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import CustomPagination from "../transaccionComponent/CustomPagination"
import ModalActualizarPrecioEntidades from "./ModalActualizarPrecioEntidades"
import ModalEditarPrecioId from "./ModalEditarPrecioId"
import { ErpContext } from "../../context/ErpContext.js"
import { getLocaleConfig } from "../../utils/numeros.js"
import ModalAsociarItemPorCliente from "./ModalAsociarItemPorCliente"
import ModalActualizacionMasiva from "./ModalActualizacionMasiva"
import TuneIcon from "@mui/icons-material/Tune"
import CloseIcon from "@mui/icons-material/Close"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import MoneyOffIcon from "@mui/icons-material/MoneyOff"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ReportProblemIcon from "@mui/icons-material/ReportProblem"
import StarIcon from "@mui/icons-material/Star"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import dayjs from "dayjs"
import jsPDF from "jspdf"
import "jspdf-autotable"
import Swal from "sweetalert2"

const theme = createTheme({
  palette: {
    primary: {
      main: "#5b5b5f",
      light: "#4791db",
      dark: "#115293",
    },
    secondary: {
      main: "#ff9800", // Naranja para costos
    },
    background: {
      default: "#f5f7fa",
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
})

export default function TablaListaCosto() {
  const {
    getListaMontosByEntidad,
    getEntidadesUnicasByFilters,
    getValoresUnicosAtributo,
    getEntidadFilterTipoEntidad,
    getItemTresLetras,
    itemTresLetras,
    arrayItemTresLetras,
    getTop10ItemVentas,
    getItemAtributoNoEliminados,
    itemAtributoNoEliminados,
    getUbicacion,
    ubicaciones,
    updateAllPrices,
    updateFilteredPrices,
    updateItemPrecioListaId,
    updatePreciosListaMultiple,
    updateMargenGananciaListaId,
    updateMargenGananciaFiltrado,
    getClientesConPreciosByFiltros,
    promoverProveedorReferente,
    actualizarPreciosClientesDesdeReferente,
    copiarPreciosEntreEntidades,
    isNavbarCollapsed,
    user,
    formatoNumericoGlobal,
  } = useContext(ErpContext)

  const localeConfig = useMemo(() => getLocaleConfig(formatoNumericoGlobal || null), [formatoNumericoGlobal])

  // Estados
  const [proveedores, setProveedores] = useState([])
  const [itemsProveedor, setItemsProveedor] = useState([])
  const [selectedProveedor, setSelectedProveedor] = useState(null)
  const [searchText, setSearchText] = useState("")
  const [topVentasItems, set10VentasItems] = useState(null)
  const [filteredItems, setFilteredItems] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [attributeValues, setAttributeValues] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)

  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const [openModal, setOpenModal] = useState(false)
  const [precioLista, setPrecioLista] = useState(0)
  const [updateSuccess, setUpdateSuccess] = useState(false)
  const [soloUltimos, setSoloUltimos] = useState(true)
  const [incluirItemsSinPrecio, setIncluirItemsSinPrecio] = useState(false)
  const [openAsociar, setOpenAsociar] = useState(false)

  const [openModalActualizar, setOpenModalActualizar] = useState(false)
  const [entidades, setEntidades] = useState([])
  const [entidadesCargadas, setEntidadesCargadas] = useState(false)

  // Edición inline de costos
  const [editMode, setEditMode] = useState(false)
  const [editedPrices, setEditedPrices] = useState({})
  const [editedMargenes, setEditedMargenes] = useState({})
  const [savingEdits, setSavingEdits] = useState(false)
  const [modifiedCount, setModifiedCount] = useState(0)
  const [modifiedMargenCount, setModifiedMargenCount] = useState(0)

  // Estados para filtros dinámicos por atributos
  const [filtrosAtributos, setFiltrosAtributos] = useState({})
  const [sugerenciasAtributos, setSugerenciasAtributos] = useState({})
  
  // Debouncing para searchText
  const [debouncedSearchText, setDebouncedSearchText] = useState("")

  // Navegación/edición tipo planilla
  const [editingRowId, setEditingRowId] = useState(null)
  const [selectAllOnEdit, setSelectAllOnEdit] = useState(true)
  const [editModeInternal, setEditModeInternal] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [shouldFocusOnEnter, setShouldFocusOnEnter] = useState(false)
  const priceInputRefs = useRef({})

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [mostrarFiltroAtributos, setMostrarFiltroAtributos] = useState(false)
  const [openModalActualizacionMasiva, setOpenModalActualizacionMasiva] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [confirmModalData, setConfirmModalData] = useState({ type: null, callback: null })

  // Debouncing para searchText
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchText])

  // Efecto para buscar items cuando el texto de búsqueda cambia (con debouncing)
  useEffect(() => {
    if (debouncedSearchText.length >= 3) {
      arrayItemTresLetras([])
      getItemTresLetras(debouncedSearchText)
    } else {
      arrayItemTresLetras([])
      arrayItemTresLetras(topVentasItems)
      setFilteredItems([])
    }
  }, [debouncedSearchText])

  // Efecto para cargar datos iniciales (sin cargar todas las entidades)
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true)
      try {
        await getItemAtributoNoEliminados()
        await getUbicacion()
        const top10items = await getTop10ItemVentas()
        if (top10items) {
          const mappedTop10 = top10items.map(({ item, ventas }) => ({
            id: item.id,
            codigo: item.codigo,
            descripcion: item.descripcion,
            codigoScanner: item.codigoScanner,
          }))
          set10VentasItems(mappedTop10)
          arrayItemTresLetras(mappedTop10)
        }
        // Solo cargar proveedores básicos, no todas las entidades
        const entidadesProveedores = await getEntidadFilterTipoEntidad(2)
        // Asegurar que siempre sea un array
        setProveedores(Array.isArray(entidadesProveedores) ? entidadesProveedores : [])
        // No cargar entidades aquí - se cargarán bajo demanda
      } catch (error) {
        console.error("Error al obtener datos iniciales:", error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchInitialData()
  }, [])

  // Efecto para cargar items iniciales
  useEffect(() => {
    fetchItems(null, null, null, true, 1, limit, null, false, {})
  }, [])

  // OPTIMIZACIÓN: Función para cargar entidades bajo demanda - SOLO PARA MODAL ASOCIAR
  const cargarEntidadesSiNoExisten = useCallback(async () => {
    if (!entidadesCargadas) {
      try {
        const entidadesProveedores = await getEntidadFilterTipoEntidad(2)
        // Asegurar que siempre sea un array
        setEntidades(Array.isArray(entidadesProveedores) ? entidadesProveedores : [])
        setEntidadesCargadas(true)
      } catch (error) {
        console.error("Error al cargar entidades:", error)
        setEntidades([]) // Asegurar que sea un array incluso en caso de error
      }
    }
  }, [entidadesCargadas, getEntidadFilterTipoEntidad])

  // Función para cargar entidades filtradas según búsqueda actual - PARA MODAL ACTUALIZAR
  const cargarEntidadesFiltradasActuales = async () => {
    try {
      const entidadesFiltradasActuales = await getEntidadesUnicasByFilters(
        selectedProveedor?.id || null,
        selectedItem?.id || null,
        selectedUbicacion?.id || null,
        2, // tipo entidad = proveedores
        soloUltimos,
        searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null,
        incluirItemsSinPrecio,
        filtrosAtributos,
      )
      // Asegurar que siempre sea un array
      setEntidades(Array.isArray(entidadesFiltradasActuales) ? entidadesFiltradasActuales : [])
    } catch (error) {
      console.error("Error al cargar entidades filtradas:", error)
      // Fallback: cargar todas las entidades de proveedores
      try {
        const entidadesProveedores = await getEntidadFilterTipoEntidad(2)
        // Asegurar que siempre sea un array
        setEntidades(Array.isArray(entidadesProveedores) ? entidadesProveedores : [])
      } catch (fallbackError) {
        console.error("Error en fallback al cargar entidades:", fallbackError)
        setEntidades([]) // Asegurar que sea un array incluso en caso de error
      }
    }
  }

  // OPTIMIZACIÓN: Memoizar si hay filtros activos (excepto soloUltimos que es el default)
  const hayFiltrosActivos = useMemo(() => {
    return !!(
      selectedProveedor ||
      selectedItem ||
      selectedUbicacion ||
      (searchText && searchText.trim() !== "") ||
      Object.values(filtrosAtributos).some((valor) => valor && valor.trim() !== "")
    )
  }, [selectedProveedor, selectedItem, selectedUbicacion, searchText, filtrosAtributos])

  // OPTIMIZACIÓN: Memoizar entidades únicas - simplificado
  // Las entidades vienen filtradas desde el backend cuando se abre el modal
  const entidadesFiltradas = useMemo(() => {
    // Asegurar que siempre retornamos un array
    const entidadesArray = Array.isArray(entidades) ? entidades : []
    const proveedoresArray = Array.isArray(proveedores) ? proveedores : []
    
    if (entidadesArray.length > 0) {
      return entidadesArray
    }
    return proveedoresArray
  }, [entidades, proveedores])

  // Función para generar etiqueta de item
  const generateItemLabel = (item) => {
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)

    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")

    return `${item.codigo} - ${item.descripcion} - ${attributeValues} - ${item.codigoScanner}`
  }

  // Efecto para selección automática de item por código de scanner
  useEffect(() => {
    if (itemTresLetras) {
      if (searchText.length >= 3 && itemTresLetras.length > 0) {
        const found = itemTresLetras.find((item) => item.codigoScanner === searchText)
        if (found) {
          const autoSelected = {
            label: generateItemLabel(found),
            descripcion: found.descripcion,
            codigo: found.codigo,
            id: found.id,
            codigoScanner: found.codigoScanner,
          }
          setSelectedItem(autoSelected)
        }
      }
    }
  }, [searchText, itemTresLetras])

  // useEffect para ejecutar búsqueda automática cuando se selecciona un item
  useEffect(() => {
    if (selectedItem) {
      console.log("Item seleccionado, ejecutando búsqueda automática:", selectedItem)
      filtrarItemProveedor()
    }
  }, [selectedItem])

  // Función para filtrar items por proveedor, item y ubicación
  const filtrarItemProveedor = async () => {
    setIsLoading(true)
    const idProveedor = selectedProveedor?.id || null
    const idItem = selectedItem?.id || null
    const idUbicacion = selectedUbicacion?.id || null

    const searchTextForMultiple = searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null

    console.log("=== FILTRAR ITEM PROVEEDOR (COSTOS) ===")
    console.log("selectedItem:", selectedItem)
    console.log("searchText:", searchText)
    console.log("Ejecutando fetchItems con filtros:", {
      idProveedor,
      idItem,
      idUbicacion,
      searchTextForMultiple,
    })

    await fetchItems(
      idProveedor,
      idItem,
      idUbicacion,
      soloUltimos,
      page,
      limit,
      searchTextForMultiple,
      incluirItemsSinPrecio,
      filtrosAtributos,
    )
    setIsLoading(false)
  }

  // Función para limpiar filtros
  const limpiarFiltros = () => {
    setSelectedProveedor(null)
    setSelectedItem(null)
    setSelectedUbicacion(null)
    setSearchText("")
    setSoloUltimos(true)
    setIncluirItemsSinPrecio(false)
    setFiltrosAtributos({})
    fetchItems(null, null, null, true, 1, limit, null, false, {})
    setPage(1)
  }

  const fetchItems = async (
    idProveedor,
    idItem,
    idUbicacion,
    soloUltimos,
    currentPage = page,
    pageLimit = limit,
    searchTextForMultiple = null,
    incluirItemsSinPrecio = false,
    atributosFilters = {},
  ) => {
    setIsLoading(true)
    setItemsProveedor([])
    try {
      const response = await getListaMontosByEntidad(
        idProveedor,
        idItem,
        idUbicacion,
        2, // Tipo entidad proveedor
        currentPage,
        pageLimit,
        soloUltimos,
        searchTextForMultiple,
        incluirItemsSinPrecio,
        atributosFilters,
      )

      const items = response.items
      console.log("=== FRONTEND COSTOS: Datos recibidos del backend ===")
      console.log("Items recibidos:", items)
      console.log("Primer item:", items?.[0])

      if (items != null) {
        const itemsTransformados = items.map((item, index) => ({
          id: item.id || `${item.idItem}-${item.idEntidad}-${item.idUbicacion}-${index}`,
          fecha: item.fecha,
          idItem: item.idItem,
          idEntidad: item.idEntidad,
          idUbicacion: item.idUbicacion,
          costo: item.costo,
          margenGanancia: item.margenGanancia != null ? parseFloat(item.margenGanancia) : null,
          esProveedorReferente: item.esProveedorReferente ?? false,
          descripcionEntidad: item.descripcionEntidad,
          ubicacion: item.ubicacion?.descripcion || item.ubicacion || "Sin ubicación",
          descripcion: item.descripcion,
          codigo: item.codigo,
          codigoScanner: item.codigoScanner,
          itemDatoAtributo1: item.itemDatoAtributo1 || null,
          itemDatoAtributo2: item.itemDatoAtributo2 || null,
          itemDatoAtributo3: item.itemDatoAtributo3 || null,
          itemDatoAtributo4: item.itemDatoAtributo4 || null,
          itemDatoAtributo5: item.itemDatoAtributo5 || null,
          itemDatoAtributo6: item.itemDatoAtributo6 || null,
          itemDatoAtributo7: item.itemDatoAtributo7 || null,
          itemDatoAtributo8: item.itemDatoAtributo8 || null,
          itemDatoAtributo9: item.itemDatoAtributo9 || null,
          itemDatoAtributo10: item.itemDatoAtributo10 || null,
          leadTime: item.leadTime,
          eliminado: item.eliminado,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        }))

        itemsTransformados.sort((a, b) => a.idItem - b.idItem)

        console.log("=== FRONTEND COSTOS: Datos transformados ===")
        console.log("Items transformados:", itemsTransformados)
        console.log("Primer item transformado:", itemsTransformados?.[0])

        setItemsProveedor(itemsTransformados)
      }
      setTotalPages(response.totalPages)
      setTotalCount(response.totalCount)
    } catch (error) {
      console.error("Error al obtener items de costos:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const openConfirmMassUpdate = (type, callback) => {
    setConfirmModalData({ type, callback })
    setOpenConfirmModal(true)
  }

  const handleConfirmMassUpdate = async () => {
    setOpenConfirmModal(false)
    if (confirmModalData.callback) {
      await confirmModalData.callback()
    }
    setConfirmModalData({ type: null, callback: null })
  }

  const getFiltersForMassUpdate = () => {
    const searchTextForMultiple = searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null
    return {
      idEntidad: selectedProveedor?.id || null,
      idItem: selectedItem?.id || null,
      idUbicacion: selectedUbicacion?.id || null,
      idTipoEntidad: 2,
      soloUltimos,
      searchText: searchTextForMultiple,
      incluirItemsSinPrecio,
      filtrosAtributos,
    }
  }

  const handleMassUpdateCostos = async (tipoAjusteVal, valorActualizarVal, actualizarPrecios, idEntidadesCliente, soloProveedoresReferentes = false) => {
    const searchTextForMultiple = searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null
    const result = await updateFilteredPrices(
      tipoAjusteVal,
      valorActualizarVal,
      selectedProveedor?.id || null,
      selectedItem?.id || null,
      selectedUbicacion?.id || null,
      2,
      soloUltimos,
      searchTextForMultiple,
      incluirItemsSinPrecio,
      filtrosAtributos,
      actualizarPrecios,
      idEntidadesCliente,
      soloProveedoresReferentes,
    )
    await filtrarItemProveedor()
    setUpdateSuccess(true)
    setTimeout(() => setUpdateSuccess(false), 3000)
    return result
  }

  const handleMassUpdateMargen = async (margenValor, tipoAjusteMargenVal, actualizarPrecios, idEntidadesCliente, soloProveedoresReferentes = false) => {
    const searchTextForMultiple = searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null
    const result = await updateMargenGananciaFiltrado(
      margenValor,
      tipoAjusteMargenVal,
      {
        idEntidad: selectedProveedor?.id || null,
        idItem: selectedItem?.id || null,
        idUbicacion: selectedUbicacion?.id || null,
        idTipoEntidad: 2,
        soloUltimos,
        searchText: searchTextForMultiple,
        incluirItemsSinPrecio,
        filtrosAtributos,
        soloProveedoresReferentes,
      },
      actualizarPrecios,
      idEntidadesCliente,
    )
    await filtrarItemProveedor()
    setUpdateSuccess(true)
    setTimeout(() => setUpdateSuccess(false), 3000)
    return result
  }

  const handleAssociate = async (idCopiar, idPegar) => {
    await copiarPreciosEntreEntidades(idCopiar, idPegar)
    await filtrarItemProveedor()
  }

  // Funciones para el modal de edición de costo individual
  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleOpenModal = (row) => {
    console.log("el row que llega es ", row)
    setPrecioLista(row)
    setOpenModal(true)
  }

  const onUpdate = async (idItem, idDeEntidad, idUbicacion, nuevoMonto, tipoAjuste) => {
    setIsLoading(true)
    try {
      await updateItemPrecioListaId(idItem, idDeEntidad ?? 1, idUbicacion ?? 1, nuevoMonto, tipoAjuste)
      setOpenModal(false)
      await filtrarItemProveedor()
      setUpdateSuccess(true)
      setTimeout(() => setUpdateSuccess(false), 3000)
    } catch (error) {
      console.error("Error al actualizar costo:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Función para abrir el modal de actualización múltiple
  const handleOpenModalActualizar = async () => {
    await cargarEntidadesFiltradasActuales()
    setOpenModalActualizar(true)
  }

  // Función para actualizar costos de entidades seleccionadas
  const onUpdateMultiple = async (idEntidades, monto, tipoAjuste) => {
    if (!monto || isNaN(monto) || idEntidades.length === 0) {
      alert("Debe seleccionar al menos una entidad y un monto válido.")
      return
    }

    setIsLoading(true)
    try {
      // Determinar si estamos en modo búsqueda múltiple
      const searchTextForMultiple = searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null

      // Preparar filtros para enviar al backend
      const filtros = {
        idProveedor: selectedProveedor?.id || null,
        idItem: selectedItem?.id || null,
        idUbicacion: selectedUbicacion?.id || null,
        soloUltimos: soloUltimos,
        searchTextForMultiple: searchTextForMultiple,
        incluirItemsSinPrecio: incluirItemsSinPrecio,
        atributosFilters: filtrosAtributos,
        hayFiltrosActivos: hayFiltrosActivos, // Para que el backend sepa si aplicar filtros o no
      }


      await updatePreciosListaMultiple(idEntidades, Number.parseFloat(monto), tipoAjuste, filtros)
      await filtrarItemProveedor()
      setUpdateSuccess(true)
      setTimeout(() => setUpdateSuccess(false), 3000)
    } catch (error) {
      console.error("Error al actualizar costos múltiples:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Manejo de edición inline
  const handlePriceChange = (rowId, value) => {
    const str = String(value ?? "").replace(",", ".")
    setEditedPrices((prev) => ({ ...prev, [rowId]: str }))
  }

  const handleMargenChange = (rowId, value) => {
    const str = String(value ?? "").replace(",", ".")
    setEditedMargenes((prev) => ({ ...prev, [rowId]: str }))
  }

  const clearEdited = () => {
    setEditedPrices({})
    setEditedMargenes({})
  }

  useEffect(() => {
    if (!itemsProveedor) {
      setModifiedCount(0)
      return
    }
    const count = Object.entries(editedPrices).filter(([rowId, val]) => {
      const row = itemsProveedor.find((r) => String(r.id) === String(rowId))
      if (!row) return false
      const newNum = val === "" || val == null ? Number.NaN : Number.parseFloat(String(val).replace(",", "."))
      const oldNum = row.costo == null ? Number.NaN : Number(row.costo)
      return Number.isFinite(newNum) && (Number.isNaN(oldNum) || newNum !== oldNum)
    }).length
    setModifiedCount(count)
  }, [editedPrices, itemsProveedor])

  useEffect(() => {
    if (!itemsProveedor) {
      setModifiedMargenCount(0)
      return
    }
    const count = Object.entries(editedMargenes).filter(([rowId, val]) => {
      const row = itemsProveedor.find((r) => String(r.id) === String(rowId))
      if (!row) return false
      const newNum = val === "" || val == null ? Number.NaN : Number.parseFloat(String(val).replace(",", "."))
      const oldNum = row.margenGanancia == null ? Number.NaN : Number(row.margenGanancia)
      return Number.isFinite(newNum) && (Number.isNaN(oldNum) || newNum !== oldNum)
    }).length
    setModifiedMargenCount(count)
  }, [editedMargenes, itemsProveedor])

  const applyEditedPrices = async () => {
    if (!itemsProveedor || itemsProveedor.length === 0) return

    const updates = Object.entries(editedPrices)
      .map(([rowId, val]) => {
        const row = itemsProveedor.find((r) => String(r.id) === String(rowId))
        if (!row) return null
        const newNum = val === "" || val == null ? Number.NaN : Number.parseFloat(String(val).replace(",", "."))
        const oldNum = row.costo == null ? Number.NaN : Number(row.costo)
        const changed = Number.isFinite(newNum) && (Number.isNaN(oldNum) || newNum !== oldNum)
        return changed
          ? { idItem: row.idItem, idEntidad: row.idEntidad ?? 1, idUbicacion: row.idUbicacion ?? 1, monto: newNum }
          : null
      })
      .filter(Boolean)

    const margenUpdates = Object.entries(editedMargenes)
      .map(([rowId, val]) => {
        const row = itemsProveedor.find((r) => String(r.id) === String(rowId))
        if (!row) return null
        const newNum = val === "" || val == null ? Number.NaN : Number.parseFloat(String(val).replace(",", "."))
        const oldNum = row.margenGanancia == null ? Number.NaN : Number(row.margenGanancia)
        const changed = Number.isFinite(newNum) && (Number.isNaN(oldNum) || newNum !== oldNum)
        return changed
          ? { idItem: row.idItem, idEntidad: row.idEntidad ?? 1, idUbicacion: row.idUbicacion ?? 1, margen: newNum }
          : null
      })
      .filter(Boolean)

    if (updates.length === 0 && margenUpdates.length === 0) {
      alert("No hay cambios para aplicar.")
      return
    }

    setSavingEdits(true)
    setIsLoading(true)
    try {
      for (const u of updates) {
        const idEntidad = u.idEntidad ?? 1
        const idUbicacion = u.idUbicacion ?? 1
        await updateItemPrecioListaId(u.idItem, idEntidad, idUbicacion, u.monto, "valor")
      }
      for (const m of margenUpdates) {
        const idEntidad = m.idEntidad ?? 1
        const idUbicacion = m.idUbicacion ?? 1
        await updateMargenGananciaListaId(m.idItem, idEntidad, idUbicacion, m.margen)
      }
      clearEdited()
      await filtrarItemProveedor()
      setUpdateSuccess(true)
      setTimeout(() => setUpdateSuccess(false), 3000)
    } catch (e) {
      console.error("Error al aplicar cambios de costos:", e)
      alert("Ocurrió un error al aplicar los cambios.")
    } finally {
      setSavingEdits(false)
      setIsLoading(false)
    }
  }

  // Enfocar y seleccionar el input solo cuando se presiona Enter
  useEffect(() => {
    if (!editMode || editingRowId == null || !shouldFocusOnEnter) return
    const ref = priceInputRefs.current[editingRowId]
    if (ref && typeof ref.focus === "function") {
      setTimeout(() => {
        try {
          ref.focus()
          if (editModeInternal) {
            const len = String(ref.value ?? "").length
            if (ref.setSelectionRange) ref.setSelectionRange(len, len)
          } else if (selectAllOnEdit) {
            if (ref.select) ref.select()
            else if (ref.setSelectionRange) ref.setSelectionRange(0, String(ref.value ?? "").length)
          } else {
            const len = String(ref.value ?? "").length
            if (ref.setSelectionRange) ref.setSelectionRange(len, len)
          }
          setShouldFocusOnEnter(false)
        } catch (_) {}
      }, 0)
    }
  }, [editingRowId, editMode, selectAllOnEdit, editModeInternal, shouldFocusOnEnter])

  // Limpiar ediciones cuando cambian los datos de la tabla
  useEffect(() => {
    clearEdited()
  }, [itemsProveedor])

  // Inicializar la primera fila para edición cuando se cargan datos y está en modo edición
  useEffect(() => {
    if (editMode && itemsProveedor.length > 0 && !editingRowId) {
      setEditingRowId(itemsProveedor[0].id)
      setSelectAllOnEdit(true)
      setEditModeInternal(false)
    }
  }, [editMode, itemsProveedor, editingRowId])

  // Funciones helper para filtros dinámicos por atributos
  const mapearAtributosACampos = () => {
    return itemAtributoNoEliminados
      .filter((attr) => !attr.eliminado)
      .map((attr, index) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        campo: `itemDatoAtributo${index + 1}`,
        index: index + 1,
      }))
  }

  const handleAtributoFilterChange = (campo, valor) => {
    setFiltrosAtributos((prev) => ({
      ...prev,
      [campo]: valor,
    }))
  }

  const cargarSugerenciasAtributo = async (campo, searchText) => {
    try {
      const valores = await getValoresUnicosAtributo(campo, searchText)
      setSugerenciasAtributos((prev) => ({
        ...prev,
        [campo]: valores,
      }))
    } catch (error) {
      console.error(`Error al cargar sugerencias del atributo ${campo}:`, error)
    }
  }

  const handleAtributoKeyDown = (event, campo, valorActual) => {
    if (event.key === "Enter") {
      event.preventDefault()
      // Asegurar que el valor actual esté sincronizado antes de aplicar filtros
      setFiltrosAtributos((prev) => {
        const nuevosFiltros = { ...prev, [campo]: valorActual }
        // Usar setTimeout para asegurar que el estado se actualice antes de buscar
        setTimeout(() => {
          fetchItems(
            selectedProveedor?.id || null,
            selectedItem?.id || null,
            selectedUbicacion?.id || null,
            soloUltimos,
            page,
            limit,
            searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null,
            incluirItemsSinPrecio,
            nuevosFiltros,
          )
        }, 0)
        return nuevosFiltros
      })
    }
  }

  const handleLimpiarFiltrosAtributos = () => {
    setFiltrosAtributos({})
    fetchItems(
      selectedProveedor?.id || null,
      selectedItem?.id || null,
      selectedUbicacion?.id || null,
      soloUltimos,
      page,
      limit,
      searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null,
      incluirItemsSinPrecio,
      {},
    )
  }

  const cantidadFiltrosAtributosActivos = Object.values(filtrosAtributos).filter(
    (valor) => valor && valor.trim() !== "",
  ).length

  const handleToggleSoloUltimos = () => {
    const newValue = !soloUltimos
    setSoloUltimos(newValue)
    if (newValue) {
      setIncluirItemsSinPrecio(false)
    }
    fetchItems(
      selectedProveedor?.id || null,
      selectedItem?.id || null,
      selectedUbicacion?.id || null,
      newValue,
      page,
      limit,
      searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null,
      newValue ? false : incluirItemsSinPrecio,
      filtrosAtributos,
    )
  }

  const handleToggleItemsSinPrecio = () => {
    const newValue = !incluirItemsSinPrecio
    setIncluirItemsSinPrecio(newValue)
    if (newValue) {
      setSoloUltimos(false)
    }
    fetchItems(
      selectedProveedor?.id || null,
      selectedItem?.id || null,
      selectedUbicacion?.id || null,
      newValue ? false : soloUltimos,
      page,
      limit,
      searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null,
      newValue,
      filtrosAtributos,
    )
  }

  const handleToggleEditMode = () => {
    const newValue = !editMode
    setEditMode(newValue)
    if (!newValue) clearEdited()
  }

  const handleAplicarFiltrosAtributos = () => {
    fetchItems(
      selectedProveedor?.id || null,
      selectedItem?.id || null,
      selectedUbicacion?.id || null,
      soloUltimos,
      page,
      limit,
      searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null,
      incluirItemsSinPrecio,
      filtrosAtributos,
    )
  }

  // Función para generar PDF de lista de costos
  const generarPDFListaCostos = async () => {
    try {
      setIsLoading(true)

      // Validar que hay datos para generar el PDF
      if (!itemsProveedor || itemsProveedor.length === 0) {
        Swal.fire({
          title: "Sin datos",
          text: "No hay datos para generar el PDF",
          icon: "warning",
          timer: 2000,
          showConfirmButton: false,
        })
        setIsLoading(false)
        return
      }

      // Obtener todos los datos sin paginación (usando un límite muy alto)
      const idProveedor = selectedProveedor?.id || null
      const idItem = selectedItem?.id || null
      const idUbicacion = selectedUbicacion?.id || null
      const searchTextForMultiple = searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null

      // Obtener todos los datos con un límite muy alto
      const response = await getListaMontosByEntidad(
        idProveedor,
        idItem,
        idUbicacion,
        2, // Tipo entidad proveedor
        1, // Página 1
        10000, // Límite muy alto para obtener todos los datos
        soloUltimos,
        searchTextForMultiple,
        incluirItemsSinPrecio,
        filtrosAtributos,
      )

      const items = response.items || []

      // Transformar datos para el PDF (similar a fetchItems)
      const itemsParaPDF = items.map((item, index) => {
        let fechaFormateada = "-"
        if (item.fecha) {
          try {
            const fecha = new Date(item.fecha)
            if (!isNaN(fecha.getTime())) {
              fechaFormateada = new Intl.DateTimeFormat("es-AR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              }).format(fecha)
            }
          } catch (error) {
            console.warn("Error al formatear fecha:", item.fecha, error)
            fechaFormateada = item.fecha || "-"
          }
        }
        return {
          id: item.id || `${item.idItem}-${item.idEntidad}-${item.idUbicacion}-${index}`,
          fecha: fechaFormateada,
          codigo: item.codigo || "",
          codigoScanner: item.codigoScanner || "",
          descripcion: item.descripcion || "",
          ubicacion: item.ubicacion?.descripcion || item.ubicacion || "Sin ubicación",
          descripcionEntidad: item.descripcionEntidad || "",
          costo: item.costo || 0,
          // Atributos dinámicos
          itemDatoAtributo1: item.itemDatoAtributo1 || null,
          itemDatoAtributo2: item.itemDatoAtributo2 || null,
          itemDatoAtributo3: item.itemDatoAtributo3 || null,
          itemDatoAtributo4: item.itemDatoAtributo4 || null,
          itemDatoAtributo5: item.itemDatoAtributo5 || null,
          itemDatoAtributo6: item.itemDatoAtributo6 || null,
          itemDatoAtributo7: item.itemDatoAtributo7 || null,
          itemDatoAtributo8: item.itemDatoAtributo8 || null,
          itemDatoAtributo9: item.itemDatoAtributo9 || null,
          itemDatoAtributo10: item.itemDatoAtributo10 || null,
        }
      })

      // Ordenar por código
      itemsParaPDF.sort((a, b) => a.codigo.localeCompare(b.codigo))

      // Calcular totales
      const totalItems = itemsParaPDF.length
      const promedioCosto = itemsParaPDF.length > 0
        ? itemsParaPDF.reduce((acc, row) => acc + (row.costo || 0), 0) / itemsParaPDF.length
        : 0

      // Crear PDF en formato apaisado
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      let yPosition = 20

      // Colores
      const darkNavy = [26, 26, 26]

      // ===========================================
      // ENCABEZADO
      // ===========================================
      doc.setFontSize(18)
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.text("Reporte de Lista de Costos", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 10

      // Fecha del reporte
      doc.setFontSize(10)
      doc.setFont("Helvetica", "normal")
      doc.setTextColor(100, 100, 100)
      const fechaReporte = dayjs().format("DD/MM/YYYY")
      doc.text(`Fecha: ${fechaReporte}`, pageWidth / 2, yPosition, { align: "center" })
      yPosition += 8

      // Filtros aplicados
      doc.setFontSize(9)
      doc.setFont("Helvetica", "italic")
      const filtros = []
      if (selectedProveedor) filtros.push(`Proveedor: ${selectedProveedor.descripcion || selectedProveedor.label || ""}`)
      if (selectedItem) filtros.push(`Item: ${selectedItem.descripcion || selectedItem.label || ""}`)
      if (selectedUbicacion) filtros.push(`Ubicación: ${selectedUbicacion.descripcion || selectedUbicacion.label || ""}`)
      if (searchText && searchText.trim() !== "") filtros.push(`Búsqueda: ${searchText}`)
      if (soloUltimos) filtros.push("Solo últimos costos")
      if (incluirItemsSinPrecio) filtros.push("Incluye items sin costo")
      if (Object.keys(filtrosAtributos).length > 0) {
        const atributosFiltros = Object.entries(filtrosAtributos)
          .filter(([_, valor]) => valor && valor.trim() !== "")
          .map(([campo, valor]) => {
            const attr = itemAtributoNoEliminados.find((a, idx) => `itemDatoAtributo${idx + 1}` === campo)
            return `${attr?.descripcion || campo}: ${valor}`
          })
        if (atributosFiltros.length > 0) {
          filtros.push(...atributosFiltros)
        }
      }

      if (filtros.length > 0) {
        doc.text(`Filtros: ${filtros.join(" | ")}`, pageWidth / 2, yPosition, { align: "center", maxWidth: pageWidth - 30 })
        yPosition += 6
      } else {
        doc.text("Filtros: Todos", pageWidth / 2, yPosition, { align: "center" })
        yPosition += 6
      }

      yPosition += 5

      // ===========================================
      // RESUMEN
      // ===========================================
      doc.setFontSize(12)
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.text("Resumen", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 8

      doc.setFontSize(9)
      doc.setFont("Helvetica", "normal")
      doc.setTextColor(60, 60, 60)

      const resumenWidth = 100
      const resumenX = (pageWidth - resumenWidth) / 2
      let resumenY = yPosition
      const resumenTextX = resumenX + 8

      // Total de items
      doc.setFont("Helvetica", "bold")
      doc.text("Total de items:", resumenTextX, resumenY)
      doc.setFont("Helvetica", "normal")
      doc.text(totalItems.toString(), resumenX + resumenWidth - 8, resumenY, { align: "right" })
      resumenY += 7

      // Promedio de costo
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(255, 152, 0) // Naranja
      doc.text("Costo promedio:", resumenTextX, resumenY)
      doc.setFont("Helvetica", "normal")
      doc.text(
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(promedioCosto),
        resumenX + resumenWidth - 8,
        resumenY,
        { align: "right" }
      )

      yPosition = resumenY + 12

      // ===========================================
      // TABLA DE COSTOS
      // ===========================================
      doc.setFontSize(12)
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.text("Lista de Costos", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 8

      // Obtener atributos activos para las columnas
      const atributosActivos = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)

      // Preparar encabezados de la tabla
      const headers = [
        "Fecha",
        "Código",
        "Descripción",
        ...atributosActivos.map((attr) => attr.descripcion),
        "Ubicación",
        "Proveedor",
        "Costo",
      ]

      // Preparar datos para la tabla
      const tableData = itemsParaPDF.map((item) => {
        const row = [
          item.fecha || "",
          item.codigo || "",
          item.descripcion || "",
          ...atributosActivos.map((_, index) => item[`itemDatoAtributo${index + 1}`] || "-"),
          item.ubicacion || "",
          item.descripcionEntidad || "",
          new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 2,
          }).format(item.costo || 0),
        ]
        return row
      })

      // Generar tabla con autoTable
      doc.autoTable({
        startY: yPosition,
        head: [headers],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: darkNavy,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 7,
        },
        bodyStyles: {
          fontSize: 6,
          textColor: [60, 60, 60],
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        columnStyles: {
          0: { cellWidth: "auto", halign: "center", minCellWidth: 25 }, // Fecha
          1: { cellWidth: "auto", halign: "center", minCellWidth: 30 }, // Código
          2: { cellWidth: "auto", halign: "left", minCellWidth: 50 }, // Descripción
          ...atributosActivos.reduce((acc, _, index) => {
            acc[3 + index] = { cellWidth: "auto", halign: "center", minCellWidth: 25 }
            return acc
          }, {}),
          [3 + atributosActivos.length]: { cellWidth: "auto", halign: "center", minCellWidth: 30 }, // Ubicación
          [4 + atributosActivos.length]: { cellWidth: "auto", halign: "left", minCellWidth: 40 }, // Proveedor
          [5 + atributosActivos.length]: { cellWidth: "auto", halign: "right", minCellWidth: 30 }, // Costo
        },
        styles: {
          cellPadding: 2,
          overflow: "linebreak",
          halign: "left",
        },
        margin: { left: 0, right: 0, top: yPosition, bottom: 25 },
        // Repetir encabezados en cada página
        showHead: "everyPage",
        // Ajustar el margen superior en páginas siguientes para aprovechar toda la altura
        didDrawPage: function (data) {
          // A partir de la primera página (donde está la tabla), ajustar el margen superior
          // En la primera página, ya tenemos yPosition, pero en páginas siguientes empezar desde arriba
          if (data.pageNumber > 1) {
            // En páginas siguientes, empezar desde muy arriba (solo dejar espacio mínimo)
            data.settings.margin.top = 5
          }
        },
      })

      // Obtener la posición Y final después de la tabla
      const finalY = doc.lastAutoTable.finalY || yPosition + 50

      // ===========================================
      // PIE DE PÁGINA
      // ===========================================
      const pieY = pageHeight - 20
      doc.setFontSize(8)
      doc.setFont("Helvetica", "normal")
      doc.setTextColor(120, 120, 120)

      const fechaHoraGeneracion = new Date().toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })

      doc.text(
        `Generado el ${fechaHoraGeneracion} por ${user?.usuario || "Usuario"}`,
        pageWidth / 2,
        pieY,
        { align: "center" }
      )

      // Número de página
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        )
      }

      // Descargar PDF
      const nombreArchivo = `Reporte_Lista_Costos_${fechaReporte.replace(/\//g, "-")}.pdf`
      doc.save(nombreArchivo)

      Swal.fire({
        title: "PDF Generado",
        text: "El reporte se ha descargado correctamente",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      })
    } catch (error) {
      console.error("Error al generar PDF:", error)
      Swal.fire({
        title: "Error",
        text: "No se pudo generar el PDF. Intente nuevamente.",
        icon: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Columnas dinámicas de atributos
  const dynamicColumns = itemAtributoNoEliminados
    .filter((attr) => !attr.eliminado)
    .map((attr, index) => ({
      field: `itemDatoAtributo${index + 1}`,
      headerName: attr.descripcion,
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Typography variant="body2">{params.value || "-"}</Typography>,
    }))

  // Definición de columnas para el DataGrid
  const columns = [
    {
      field: "fecha",
      headerName: "Fecha",
      width: 100,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "codigo",
      headerName: "Código",
      width: 120,
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 500,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          }}
        />
      ),
    },
    {
      field: "codigoScanner",
      headerName: "Código de Barras",
      width: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      headerAlign: "center",
      width: 220,
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    ...dynamicColumns,
    {
      field: "ubicacion",
      headerName: "Ubicación",
      headerAlign: "center",
      width: 180,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarehouseIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "descripcionEntidad",
      headerName: "Proveedor",
      headerAlign: "center",
      width: 200,
      flex: 1.5,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "esProveedorReferente",
      headerName: "Ref",
      width: 56,
      flex: 0,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.esProveedorReferente ? "Proveedor referente" : "No referente"}>
          {params.row.esProveedorReferente ? (
            <StarIcon sx={{ color: "rgb(244,147,14)", fontSize: 18 }} />
          ) : (
            <StarBorderIcon sx={{ color: "text.disabled", fontSize: 18 }} />
          )}
        </Tooltip>
      ),
    },
    {
      field: "costo",
      headerName: "Costo",
      width: 130,
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => {
        const rowId = params.row.id
        const original = params.value
        const edited = editedPrices[rowId]
        const editedNum =
          edited === undefined || edited === "" ? null : Number.parseFloat(String(edited).replace(",", "."))
        const isChanged =
          edited !== undefined && edited !== "" && Number.isFinite(editedNum) && editedNum !== Number(original)

        if (editMode) {
          return (
            <TextField
              size="small"
              type="number"
              defaultValue={edited !== undefined ? edited : (original ?? "")}
              onBlur={(e) => handlePriceChange(rowId, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  const value = e.currentTarget.value
                  handlePriceChange(rowId, value)
                  const idx = itemsProveedor.findIndex((r) => String(r.id) === String(rowId))
                  const next = idx >= 0 && idx + 1 < itemsProveedor.length ? itemsProveedor[idx + 1] : null
                  if (next) {
                    setEditingRowId(next.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(true)
                  }
                }
                if (e.key === "ArrowDown") {
                  e.preventDefault()
                  const value = e.currentTarget.value
                  handlePriceChange(rowId, value)
                  const idx = itemsProveedor.findIndex((r) => String(r.id) === String(rowId))
                  const next = idx >= 0 && idx + 1 < itemsProveedor.length ? itemsProveedor[idx + 1] : null
                  if (next) {
                    setEditingRowId(next.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(false)
                  }
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault()
                  const value = e.currentTarget.value
                  handlePriceChange(rowId, value)
                  const idx = itemsProveedor.findIndex((r) => String(r.id) === String(rowId))
                  const prev = idx > 0 ? itemsProveedor[idx - 1] : null
                  if (prev) {
                    setEditingRowId(prev.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(false)
                  }
                }
              }}
              onFocus={(e) => {
                try {
                  if (selectAllOnEdit && !editModeInternal && e.target && e.target.select) {
                    e.target.select()
                  }
                } catch (_) {}
              }}
              onMouseDown={(e) => {
                if (e && typeof e.detail === "number") {
                  setSelectAllOnEdit(e.detail !== 2)
                }
              }}
              inputProps={{
                step: "0.01",
                style: {
                  color: isChanged ? "rgb(244, 177, 14)" : theme.palette.secondary.main,
                  fontWeight: 700,
                },
              }}
              inputRef={(el) => {
                priceInputRefs.current[rowId] = el
              }}
              sx={{ width: "100%" }}
            />
          )
        }

        return (
          <Typography
            variant="body2"
            sx={{
              color: params.value === null ? theme.palette.text.secondary : theme.palette.secondary.main,
              fontWeight: "bold",
              backgroundColor:
                params.value === null
                  ? alpha(theme.palette.text.secondary, 0.1)
                  : alpha(theme.palette.secondary.main, 0.1),
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            {params.value === null ? "-" : `$${params.value?.toLocaleString("es-AR") || "0.00"}`}
          </Typography>
        )
      },
    },
    {
      field: "margenGanancia",
      headerName: "Margen %",
      width: 130,
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => {
        const rowId = params.row.id
        const original = params.value
        const edited = editedMargenes[rowId]
        const editedNum =
          edited === undefined || edited === "" ? null : Number.parseFloat(String(edited).replace(",", "."))
        const isChanged =
          edited !== undefined && edited !== "" && Number.isFinite(editedNum) && editedNum !== Number(original)

        if (editMode) {
          return (
            <TextField
              size="small"
              type="number"
              defaultValue={edited !== undefined ? edited : (original ?? "")}
              onBlur={(e) => handleMargenChange(rowId, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleMargenChange(rowId, e.currentTarget.value)
                  const idx = itemsProveedor.findIndex((r) => String(r.id) === String(rowId))
                  const next = idx >= 0 && idx + 1 < itemsProveedor.length ? itemsProveedor[idx + 1] : null
                  if (next) {
                    setEditingRowId(next.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(true)
                  }
                }
                if (e.key === "ArrowDown") {
                  e.preventDefault()
                  handleMargenChange(rowId, e.currentTarget.value)
                  const idx = itemsProveedor.findIndex((r) => String(r.id) === String(rowId))
                  const next = idx >= 0 && idx + 1 < itemsProveedor.length ? itemsProveedor[idx + 1] : null
                  if (next) {
                    setEditingRowId(next.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(false)
                  }
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault()
                  handleMargenChange(rowId, e.currentTarget.value)
                  const idx = itemsProveedor.findIndex((r) => String(r.id) === String(rowId))
                  const prev = idx > 0 ? itemsProveedor[idx - 1] : null
                  if (prev) {
                    setEditingRowId(prev.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(false)
                  }
                }
              }}
              onFocus={(e) => {
                try {
                  if (selectAllOnEdit && !editModeInternal && e.target && e.target.select) {
                    e.target.select()
                  }
                } catch (_) {}
              }}
              inputProps={{
                step: "0.01",
                style: {
                  color: isChanged ? "rgb(244, 177, 14)" : "#4caf50",
                  fontWeight: 700,
                },
              }}
              sx={{ width: "100%" }}
            />
          )
        }

        return (
          <Typography
            variant="body2"
            sx={{
              color: params.value == null ? theme.palette.text.secondary : "#4caf50",
              fontWeight: "bold",
              backgroundColor:
                params.value == null
                  ? alpha(theme.palette.text.secondary, 0.1)
                  : alpha("#4caf50", 0.1),
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            {params.value == null ? "-" : `${Number(params.value).toFixed(2)}%`}
          </Typography>
        )
      },
    },
    {
      field: "action",
      headerName: "Acciones",
      width: 100,
      flex: 0.3,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleOpenModal(params.row)}
          sx={{
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
            },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ]

  // Helper para contar filtros activos totales
  const contarFiltrosActivos = () => {
    let count = 0
    if (selectedProveedor) count++
    if (selectedItem) count++
    if (selectedUbicacion) count++
    if (searchText && searchText.trim() !== "") count++
    if (!soloUltimos) count++
    if (incluirItemsSinPrecio) count++
    count += cantidadFiltrosAtributosActivos
    return count
  }

  const totalFiltrosActivos = contarFiltrosActivos()

  const handleItemChange = (event, newValue) => {
    if (typeof newValue === "string") {
      setSearchText(newValue)
      setSelectedItem(null)
    } else {
      setSelectedItem(newValue)
      if (newValue) {
        setSearchText(newValue.label)
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: "#f5f5f5",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            borderRadius: "8px 8px 0 0",
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
            }}
          >
            {/* Búsqueda de Ítem */}
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                fullWidth
                size="small"
                disablePortal
                freeSolo
                options={(itemTresLetras || []).map((it) => ({
                  label: generateItemLabel(it),
                  descripcion: it.descripcion,
                  codigo: it.codigo,
                  id: it.id,
                  codigoScanner: it.codigoScanner,
                }))}
                value={selectedItem}
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    setSearchText(newValue)
                    setSelectedItem(null)
                  } else {
                    handleItemChange(event, newValue)
                    setSelectedItem(newValue)
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setSearchText(newInputValue)
                  if (!newInputValue || newInputValue.trim() === "") {
                    setSelectedItem(null)
                  }
                }}
                inputValue={searchText}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar ítem"
                    placeholder="Código, descripción o barras"
                    onKeyPress={(event) => {
                      if (event.key === "Enter" && searchText && searchText.trim() !== "" && !selectedItem) {
                        event.preventDefault()
                        filtrarItemProveedor()
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (selectedItem || (searchText && searchText.trim() !== "")) && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedItem(null)
                              setSearchText("")
                            }}
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Proveedor */}
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                fullWidth
                size="small"
                options={proveedores}
                getOptionLabel={(option) => `${option.descripcion + " " + option.apellido}`}
                value={selectedProveedor}
                onChange={(event, newValue) => setSelectedProveedor(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Proveedor"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Ubicación */}
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                fullWidth
                size="small"
                disablePortal
                options={ubicaciones.map((it) => ({
                  id: it.id,
                  label: it.descripcion,
                }))}
                value={selectedUbicacion}
                onChange={(event, newValue) => {
                  setSelectedUbicacion(newValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ubicación"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <WarehouseIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Botones de acción */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={filtrarItemProveedor}
                startIcon={<SearchIcon />}
                disabled={isLoading}
              >
                Buscar
              </Button>
              <Button variant="outlined" onClick={limpiarFiltros} startIcon={<FilterAltOffIcon />} disabled={isLoading}>
                Limpiar
              </Button>
              <Tooltip title="Descargar PDF">
                <IconButton
                  onClick={generarPDFListaCostos}
                  disabled={isLoading || !itemsProveedor || itemsProveedor.length === 0}
                  size="small"
                  sx={{
                    bgcolor: alpha("#d32f2f", 0.1),
                    "&:hover": { bgcolor: alpha("#d32f2f", 0.2) },
                    "&:disabled": { bgcolor: alpha("#d32f2f", 0.05) },
                  }}
                >
                  <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={filterDrawerOpen ? "Ocultar filtros avanzados" : "Mostrar más filtros"}>
                <Badge badgeContent={contarFiltrosActivos()} color="primary">
                  <IconButton
                    onClick={() => setFilterDrawerOpen(!filterDrawerOpen)}
                    size="medium"
                    sx={{
                      color: theme.palette.primary.main,
                      border: `1px solid ${alpha(theme.palette.primary.main, filterDrawerOpen ? 0.5 : 0.3)}`,
                      bgcolor: filterDrawerOpen ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                      },
                    }}
                  >
                    <TuneIcon />
                  </IconButton>
                </Badge>
              </Tooltip>
            </Box>
          </Box>

          {mostrarFiltroAtributos && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                pt: 2,
              }}
            >
              {/* Filtros por Atributos */}
              {mostrarFiltroAtributos && itemAtributoNoEliminados && itemAtributoNoEliminados.length > 0 && (
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      Filtros por Atributos
                    </Typography>
                    <IconButton size="small" onClick={() => setMostrarFiltroAtributos(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
                      gap: 2,
                    }}
                  >
                    {mapearAtributosACampos().map((atributo) => (
                      <Autocomplete
                        key={atributo.campo}
                        freeSolo
                        options={sugerenciasAtributos[atributo.campo] || []}
                        value={filtrosAtributos[atributo.campo] || ""}
                        onInputChange={(event, newValue) => {
                          handleAtributoFilterChange(atributo.campo, newValue)
                          // Cargar sugerencias cuando el usuario escribe (mínimo 2 caracteres)
                          if (newValue && newValue.length >= 2) {
                            cargarSugerenciasAtributo(atributo.campo, newValue)
                          }
                        }}
                        onChange={(event, newValue) => {
                          handleAtributoFilterChange(atributo.campo, newValue || "")
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={atributo.descripcion}
                            size="small"
                            placeholder="Buscar..."
                            onKeyDown={(event) => {
                              // Obtener el valor actual del input directamente
                              const valorActual = event.target.value || ""
                              handleAtributoKeyDown(event, atributo.campo, valorActual)
                            }}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: filtrosAtributos[atributo.campo]
                                  ? alpha(theme.palette.primary.main, 0.05)
                                  : "white",
                              },
                            }}
                          />
                        )}
                      />
                    ))}
                  </Box>

                  {cantidadFiltrosAtributosActivos > 0 && (
                    <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={handleAplicarFiltrosAtributos}
                        sx={{ textTransform: "none" }}
                      >
                        Aplicar Filtros
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleLimpiarFiltrosAtributos}
                        sx={{ textTransform: "none" }}
                      >
                        Limpiar
                      </Button>
                    </Box>
                  )}
                </Paper>
              )}

            </Box>
          )}
        </Box>

        {/* Sección independiente para modo edición */}
        {editMode && (
          <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                  Modo Edición Activo
                </Typography>
                {modifiedCount > 0 && (
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main }}>
                    {modifiedCount} costo{modifiedCount !== 1 ? 's' : ''} modificado{modifiedCount !== 1 ? 's' : ''}
                  </Typography>
                )}
                {modifiedMargenCount > 0 && (
                  <Typography variant="body2" sx={{ color: "#4caf50" }}>
                    {modifiedMargenCount} margen{modifiedMargenCount !== 1 ? 'es' : ''} modificado{modifiedMargenCount !== 1 ? 's' : ''}
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={applyEditedPrices}
                    disabled={savingEdits || (modifiedCount === 0 && modifiedMargenCount === 0)}
                    sx={{ textTransform: "none" }}
                  >
                    Guardar cambios ({modifiedCount + modifiedMargenCount})
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={clearEdited}
                    startIcon={<ClearIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        <Drawer
          anchor="right"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          variant="persistent"
          sx={{
            "& .MuiDrawer-paper": {
              width: 80,
              top: 0,
              height: "100vh",
              backgroundColor: alpha(theme.palette.background.paper, 0.98),
              borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1200,
              overflowX: "hidden",
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
                pb: 1,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <IconButton size="small" onClick={() => setFilterDrawerOpen(false)}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
              {/* Filtros simples que ejecutan inmediatamente */}
              <Tooltip title="Solo último costo" placement="left">
                <IconButton
                  onClick={handleToggleSoloUltimos}
                  sx={{
                    color: soloUltimos ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                    "& svg": {
                      color: soloUltimos ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                    },
                  }}
                >
                  <CheckBoxIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Items sin costo" placement="left">
                <IconButton
                  onClick={handleToggleItemsSinPrecio}
                  sx={{
                    color: incluirItemsSinPrecio ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                    "& svg": {
                      color: incluirItemsSinPrecio ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                    },
                  }}
                >
                  <MoneyOffIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Modo edición de costos" placement="left">
                <IconButton
                  onClick={handleToggleEditMode}
                  sx={{
                    color: editMode ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                    "& svg": {
                      color: editMode ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>

              <Divider sx={{ my: 1 }} />

              {/* Filtros avanzados */}
              <Tooltip title="Filtrar por atributos" placement="left">
                <IconButton
                  onClick={() => setMostrarFiltroAtributos(!mostrarFiltroAtributos)}
                  sx={{
                    color: mostrarFiltroAtributos ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                    "& svg": {
                      color: mostrarFiltroAtributos ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                    },
                  }}
                >
                  <Badge badgeContent={cantidadFiltrosAtributosActivos} color="primary">
                    <FilterAltIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title="Actualización masiva de costos y márgenes" placement="left">
                <IconButton
                  onClick={() => setOpenModalActualizacionMasiva(true)}
                  sx={{
                    color: "rgba(0, 0, 0, 0.54) !important",
                    "& svg": {
                      color: "rgba(0, 0, 0, 0.54) !important",
                    },
                  }}
                >
                  <PriceChangeIcon />
                </IconButton>
              </Tooltip>

              <Divider sx={{ my: 1 }} />

              {/* Botones que abren modales */}
              <Tooltip title="Actualizar por entidades" placement="left">
                <IconButton
                  onClick={handleOpenModalActualizar}
                  sx={{
                    color: "rgba(0, 0, 0, 0.54) !important",
                    "& svg": {
                      color: "rgba(0, 0, 0, 0.54) !important",
                    },
                  }}
                >
                  <PersonIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Asociar costos entre entidades" placement="left">
                <IconButton
                  onClick={async () => {
                    await cargarEntidadesSiNoExisten()
                    setOpenAsociar(true)
                  }}
                  sx={{
                    color: "rgba(0, 0, 0, 0.54) !important",
                    "& svg": {
                      color: "rgba(0, 0, 0, 0.54) !important",
                    },
                  }}
                >
                  <SwapHorizIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Drawer>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
      

          {/* Tabla */}
          <Box sx={{ flex: 1, overflow: "hidden", p: 2 }}>
            <Paper
              sx={{
                height: "100%",
                width: "100%",
                boxShadow: "none",
                border: "1px solid rgba(0,0,0,0.08)",
                borderTop: "none",
              }}
            >
              <DataGrid
                key={isNavbarCollapsed ? "collapsed" : "expanded"}
                className="tablaVentasDataGrid"
                rows={itemsProveedor}
                columns={columns}
                getRowId={(row) => row.id}
                rowCount={totalCount}
                loading={isLoading}
                paginationMode="server"
                disableColumnMenu
                disableSelectionOnClick
                getRowHeight={() => 35}
                headerHeight={35}
                autoHeight={false}
                onCellClick={(params) => {
                  if (editMode && params.field === "costo") {
                    setEditingRowId(params.row.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(false)
                  }
                }}
                onCellDoubleClick={(params) => {
                  if (editMode && params.field === "costo") {
                    setEditingRowId(params.row.id)
                    setSelectAllOnEdit(false)
                    setEditModeInternal(true)
                    setShouldFocusOnEnter(false)
                  }
                }}
                onCellKeyDown={(params, event) => {
                  if (!editMode) return
                  if (params.field === "costo" && event.key === "Enter") {
                    event.preventDefault()
                    setEditingRowId(params.row.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(true)
                  }
                  if (params.field === "costo" && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
                    event.preventDefault()
                    setEditingRowId(params.row.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(false)
                  }
                }}
                onRowKeyDown={(params, event) => {
                  if (!editMode) return

                  if (event.key === "ArrowDown") {
                    event.preventDefault()
                    const idx = itemsProveedor.findIndex((r) => String(r.id) === String(params.row.id))
                    const next = idx >= 0 && idx + 1 < itemsProveedor.length ? itemsProveedor[idx + 1] : null
                    if (next) {
                      setEditingRowId(next.id)
                      setSelectAllOnEdit(true)
                      setEditModeInternal(false)
                      setShouldFocusOnEnter(false)
                    }
                  }
                  if (event.key === "ArrowUp") {
                    event.preventDefault()
                    const idx = itemsProveedor.findIndex((r) => String(r.id) === String(params.row.id))
                    const prev = idx > 0 ? itemsProveedor[idx - 1] : null
                    if (prev) {
                      setEditingRowId(prev.id)
                      setSelectAllOnEdit(true)
                      setEditModeInternal(false)
                      setShouldFocusOnEnter(false)
                    }
                  }
                  if (event.key === "Enter") {
                    event.preventDefault()
                    setEditingRowId(params.row.id)
                    setSelectAllOnEdit(true)
                    setEditModeInternal(false)
                    setShouldFocusOnEnter(true)
                  }
                }}
                localeText={{
                  noRowsLabel: selectedProveedor
                    ? "No se encontraron registros para los filtros seleccionados"
                    : "Seleccione un proveedor para ver sus costos",
                  noResultsOverlayLabel: "No se encontraron resultados",
                  loadingLabel: "Cargando...",
                }}
                sx={{
                  width: "100%",
                  border: 0,
                  "& .MuiDataGrid-footerContainer": {
                    display: "none !important",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#5b5b5f",
                    color: "white",
                    borderBottom: "2px solid #333",
                    height: "45px !important",
                    minHeight: "45px !important",
                    maxHeight: "45px !important",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    height: "45px !important",
                    minHeight: "45px !important",
                    maxHeight: "45px !important",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: "white !important",
                    lineHeight: "45px !important",
                  },
                  "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                    color: "white !important",
                  },
                  "& .MuiDataGrid-menuIconButton": {
                    color: "rgb(244, 177, 14) !important",
                  },
                  "& .MuiInputBase-root": {
                    color: "rgb(244, 177, 14) !important",
                  },
                  "& .MuiTablePagination-root": {
                    color: "black !important",
                  },
                  "& .MuiSelect-icon": {
                    color: "rgb(244, 177, 14) !important",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #ccc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  "& .MuiDataGrid-row": {
                    "&:nth-of-type(even)": {
                      backgroundColor: "#F7F7F7",
                    },
                    "&:hover": {
                      backgroundColor: "#E3EAFD",
                      transition: "background 0.3s ease",
                    },
                  },
                  "& .row-devolucion": {
                    backgroundColor: alpha("#9e9e9e !important", 0.05),
                    "&:hover": {
                      backgroundColor: alpha("#9e9e9e !important", 0.1),
                    },
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    lineHeight: "none !important",
                    height: "10px !imporant",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "#ffffff",
                    borderTop: "1px solid #ddd",
                    zIndex: 10,
                  },
                  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                    width: "2px",
                  },
                  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "10px",
                  },
                }}
                components={{
                  Pagination: () => null,
                }}
              />
            </Paper>
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            p: 1.5,
            backgroundColor: "#f5f5f5",
            borderRadius: "0 0 8px 8px",
            borderTop: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={null}
            vieneDe="itemProveedor"
            onPageChange={(newPage) => {
              setPage(newPage)
              const searchTextForMultiple =
                searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null
              fetchItems(
                selectedProveedor?.id,
                selectedItem?.id,
                selectedUbicacion?.id,
                soloUltimos,
                newPage,
                limit,
                searchTextForMultiple,
                incluirItemsSinPrecio,
                filtrosAtributos,
              )
            }}
          />
        </Box>

        {/* Alerta de éxito */}
        {updateSuccess && (
          <Fade in={updateSuccess}>
            <Alert
              severity="success"
              variant="filled"
              sx={{
                position: "fixed",
                top: 16,
                right: 16,
                zIndex: 10000,
                boxShadow: 3,
              }}
            >
              Costos actualizados correctamente
            </Alert>
          </Fade>
        )}

        {/* Modales */}
        <ModalEditarPrecioId
          open={openModal}
          handleClose={handleCloseModal}
          precioLista={precioLista}
          onUpdate={onUpdate}
          onUpdateMargen={async (idItem, idEntidad, idUbicacion, margen) => {
            await updateMargenGananciaListaId(idItem, idEntidad, idUbicacion, margen)
            await filtrarItemProveedor()
          }}
          onPromoverReferente={async (idItem, idEntidad, idUbicacion, monto, tipoAjuste, margenGanancia, idEntidadesCliente) => {
            await promoverProveedorReferente(idItem, idEntidad, idUbicacion, monto, tipoAjuste, margenGanancia, idEntidadesCliente)
            await filtrarItemProveedor()
            setUpdateSuccess(true)
            setTimeout(() => setUpdateSuccess(false), 3000)
          }}
          onActualizarPreciosClientes={async (idItem, idEntidad, idUbicacion, idEntidadesCliente) => {
            await actualizarPreciosClientesDesdeReferente(idItem, idEntidad, idUbicacion, idEntidadesCliente)
            await filtrarItemProveedor()
            setUpdateSuccess(true)
            setTimeout(() => setUpdateSuccess(false), 3000)
          }}
          getClientesConPreciosByFiltros={getClientesConPreciosByFiltros}
          localeConfig={localeConfig}
        />

        <ModalActualizarPrecioEntidades
          open={openModalActualizar}
          handleClose={() => setOpenModalActualizar(false)}
          entidades={entidadesFiltradas}
          onUpdate={onUpdateMultiple}
        />

        <ModalAsociarItemPorCliente
          open={openAsociar}
          handleClose={() => setOpenAsociar(false)}
          entidades={entidadesFiltradas}
          onAssociate={handleAssociate}
        />

        <ModalActualizacionMasiva
          open={openModalActualizacionMasiva}
          handleClose={() => setOpenModalActualizacionMasiva(false)}
          hayFiltrosActivos={hayFiltrosActivos}
          filtros={getFiltersForMassUpdate()}
          getClientesConPreciosByFiltros={getClientesConPreciosByFiltros}
          onUpdateCostos={handleMassUpdateCostos}
          onUpdateMargen={handleMassUpdateMargen}
        />

        {/* Modal de confirmación para cambios masivos */}
        <Dialog
          open={openConfirmModal}
          onClose={() => setOpenConfirmModal(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 1,
              border: hayFiltrosActivos ? undefined : `2px solid #d32f2f`,
              backgroundColor: hayFiltrosActivos ? "#ffffff" : "#fffbfb",
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 0 }}>
            {!hayFiltrosActivos && (
              <ReportProblemIcon sx={{ color: "#d32f2f", fontSize: 36 }} />
            )}
            <Typography variant="h6" component="span" sx={{ color: !hayFiltrosActivos ? "#d32f2f" : "inherit" }}>
              {hayFiltrosActivos
                ? "Confirmar cambio masivo"
                : "¡Atención! Cambio masivo sin filtros"}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {hayFiltrosActivos
                ? "Estás por efectuar un cambio en todos los registros que coinciden con los filtros activos. ¿Estás seguro?"
                : "Estás por ejecutar un cambio masivo en TODOS los productos. Esta acción afectará a todo el catálogo. ¿Estás seguro?"}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
            <Button onClick={() => setOpenConfirmModal(false)} variant="outlined" sx={{ textTransform: "none" }}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmMassUpdate}
              variant="contained"
              color={hayFiltrosActivos ? "primary" : "error"}
              sx={{ textTransform: "none" }}
            >
              Sí, aplicar cambios
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  )
}
